import { DocumentIcon } from "@heroicons/react/24/outline";
import { Avatar, Button } from "@heroui/react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BsChatRightText } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import {
  DealResponseDto,
  FileMetadataDto,
  MessageResponseDto,
} from "../../types/types";
import { formatTimestamp } from "../../utils/utilMethods";
import { formatDayLabel, getDayKey } from "../../utils/chat/chatDates";
import { parseDealStatusMessage } from "../../utils/chat/dealStatusMessage";
import { getDealStatusBannerContent } from "../../utils/chat/dealStatusText";
import { MessageContent } from "./messageContent";
import { SystemMessageBanner } from "./systemMessageBanner";
import { VIDEO_UPLOAD_ENABLED } from "../../config/featureFlags";

interface ChatMessagesProps {
  selectedChatType: string;
  selectedInvite: any;
  messages: MessageResponseDto[];
  fetchingMessages: boolean;
  isDragging: boolean;
  handleAcceptInvite: (id: number) => void;
  handleDeclineInvite: (id: number) => void;
  chatAreaRef: React.RefObject<HTMLDivElement | any>;
  connectStatus: string | null;
  setConnectStatus: React.Dispatch<React.SetStateAction<string | null>>;
  messagesEndRef: React.RefObject<HTMLDivElement | any>;
  isMessageFromCurrentUser: (msg: MessageResponseDto) => boolean;
  selectedImage: string | null;
  setSelectedImage: React.Dispatch<React.SetStateAction<string | null>>;
  selectedVideo: string | null;
  setSelectedVideo: React.Dispatch<React.SetStateAction<string | null>>;
  imageCarousel: { images: FileMetadataDto[]; currentIndex: number } | null;
  setImageCarousel: React.Dispatch<
    React.SetStateAction<{
      images: FileMetadataDto[];
      currentIndex: number;
    } | null>
  >;
  handleWithdrawDeal: (deal: DealResponseDto) => Promise<void>;
  dealProcessing: Record<string, boolean>;
  getCurrentUserRole: () => string;
  loggedInUserName: string;
  selectedUser: any;
  selectedGroup: any;
  setSelectedDealForView: React.Dispatch<
    React.SetStateAction<DealResponseDto | null>
  >;
  setShowViewDealModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  formatText: (text: string) => string;
  onViewProfile: (profileId?: string | number | null) => void;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  selectedChatType,
  selectedInvite,
  messages,
  fetchingMessages,
  isDragging,
  connectStatus,
  setConnectStatus,
  handleAcceptInvite,
  handleDeclineInvite,
  chatAreaRef,
  messagesEndRef,
  isMessageFromCurrentUser,
  selectedImage,
  setSelectedImage,
  selectedVideo,
  setSelectedVideo,
  imageCarousel,
  setImageCarousel,
  handleWithdrawDeal,
  dealProcessing,
  getCurrentUserRole,
  loggedInUserName,
  selectedUser,
  selectedGroup,
  setSelectedDealForView,
  setShowViewDealModal,
  setShowModal,
  formatText,
  onViewProfile,
}) => {
  const { t, i18n } = useTranslation("chat");
  const navigate = useNavigate();

  const sortedMessages = useMemo(() => {
    if (!Array.isArray(messages)) return [];
    return [...messages].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [messages]);

  // Calendar day of every message, computed once per message list.
  const dayKeys = useMemo(
    () => sortedMessages.map((m) => getDayKey(m.createdAt)),
    [sortedMessages],
  );

  const renderDateDivider = (label: string) => (
    <div className="flex items-center justify-center my-6">
      <div className="flex-1 border-t border-gray-200" />
      <div className="px-4 py-1">
        <span className="text-xs text-gray-400 font-medium font-['Plus_Jakarta_Sans']">
          {label}
        </span>
      </div>
      <div className="flex-1 border-t border-gray-200" />
    </div>
  );

  const renderMessage = (msg: MessageResponseDto, index: number) => {
    const dayKey = dayKeys[index];
    const showDateDivider = index === 0 || dayKeys[index - 1] !== dayKey;
    const dayLabel = showDateDivider
      ? formatDayLabel(msg.createdAt, i18n.language, t)
      : "";
    const currentTimestamp = formatTimestamp(msg.createdAt);
    const nextMessage =
      index < sortedMessages.length - 1 ? sortedMessages[index + 1] : null;
    const nextTimestamp = nextMessage
      ? formatTimestamp(nextMessage.createdAt)
      : null;
    const showTimestamp =
      !nextMessage ||
      dayKeys[index + 1] !== dayKey ||
      nextTimestamp !== currentTimestamp;

    const dealStatus = msg.dealResponseDto
      ? null
      : parseDealStatusMessage(msg.message);

    if (dealStatus || msg.type === "SYSTEM") {
      const banner = dealStatus
        ? getDealStatusBannerContent(dealStatus, msg.sender, t)
        : null;
      const routeDealId = banner?.routeDealId;

      return (
        <div
          key={msg.id || `msg-${index}`}
          className="max-w-full overflow-hidden"
        >
          {showDateDivider && renderDateDivider(dayLabel)}
          <SystemMessageBanner
            text={banner ? banner.text : msg.message}
            onClick={
              routeDealId ? () => navigate(`/deal/view/${routeDealId}`) : undefined
            }
            timestamp={!banner && showTimestamp ? currentTimestamp : undefined}
          />
        </div>
      );
    }

    const isCurrentUser = isMessageFromCurrentUser(msg);
    const showAvatar = !isCurrentUser;

    return (
      <div
        key={msg.id || `msg-${index}`}
        className="max-w-full overflow-hidden"
      >
        {showDateDivider && renderDateDivider(dayLabel)}
        <div
          className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} mb-2 max-w-full overflow-hidden`}
        >
          {showAvatar && (
            <button
              type="button"
              onClick={() => onViewProfile(msg.sender?.id)}
              className="mr-2 flex-shrink-0 relative rounded-full outline-none focus:outline-none focus:ring-0 mt-0.5"
            >
              <Avatar
                src={msg.sender?.profileImage || undefined}
                name={`${msg.sender?.firstName} ${msg.sender?.lastName}`.trim()}
                className="w-8 h-8 text-xs"
              >
                {!msg.sender?.profileImage &&
                  `${msg.sender?.firstName?.[0] || ""}${msg.sender?.lastName?.[0] || ""}`.toUpperCase()}
              </Avatar>
            </button>
          )}
          <div
            className={`flex flex-col ${msg.dealResponseDto ? "w-full max-w-[400px]" : "max-w-xs md:max-w-md"} ${isCurrentUser ? "items-end" : "items-start"} overflow-hidden`}
          >
            {!isCurrentUser && selectedGroup && (
              <button
                type="button"
                onClick={() => onViewProfile(msg.sender?.id)}
                className="text-xs text-gray-500 mb-1 ml-1 text-start break-words hover:text-blue-600 outline-none focus:outline-none focus:ring-0 font-['Plus_Jakarta_Sans']"
              >
                {msg.sender.firstName} {msg.sender.lastName}
              </button>
            )}
            <div
              className={`max-w-full overflow-hidden ${
                msg.dealResponseDto
                  ? "w-full"
                  : `p-3.5 text-base font-normal font-['Plus_Jakarta_Sans'] leading-6 ${
                      isCurrentUser
                        ? "bg-blue-600 chat-bubble-current !text-white rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl shadow-sm"
                        : "bg-gray-200 text-gray-900 rounded-tr-2xl rounded-bl-2xl rounded-br-2xl"
                    }`
              }`}
            >
              <MessageContent
                message={msg}
                isCurrentUser={isCurrentUser}
                setSelectedImage={setSelectedImage}
                setSelectedVideo={setSelectedVideo}
                setImageCarousel={setImageCarousel}
                connectStatus={connectStatus}
                setConnectStatus={setConnectStatus}
                handleWithdrawDeal={handleWithdrawDeal}
                dealProcessing={dealProcessing}
                getCurrentUserRole={getCurrentUserRole}
                setSelectedDealForView={setSelectedDealForView}
                setShowViewDealModal={setShowViewDealModal}
                setShowModal={setShowModal}
                formatText={formatText}
              />
            </div>
            {showTimestamp && (
              <span
                className={`text-xs font-normal mt-1 text-gray-400 font-['Plus_Jakarta_Sans'] leading-4 ${isCurrentUser ? "text-right" : "text-left"}`}
              >
                {currentTimestamp}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={chatAreaRef}
      className="flex-1 min-h-0 bg-[#F8F9FA] p-4 md:p-6 overflow-y-auto overflow-x-hidden no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
    >
      {isDragging && (
        <div className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-500 z-50 flex items-center justify-center">
          <div className="text-center">
            <DocumentIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-blue-700 font-['Plus_Jakarta_Sans']">
              {t("dropFiles", "Drop files here")}
            </p>
            <p className="text-sm text-blue-600 mt-1 font-['Plus_Jakarta_Sans']">
              {t(VIDEO_UPLOAD_ENABLED ? "fileLimits" : "fileLimitsWithoutVideo", "Max file size: 50MB")}
            </p>
          </div>
        </div>
      )}

      {selectedInvite && !selectedUser && !selectedGroup ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-6 bg-white rounded-3xl m-4 border border-gray-100 shadow-sm">
          <button
            type="button"
            onClick={() => onViewProfile(selectedInvite.contactedBy.id)}
            className="mb-4 rounded-full outline-none focus:outline-none focus:ring-0"
          >
            <Avatar
              src={selectedInvite.contactedBy.profileImage}
              name={`${selectedInvite.contactedBy.firstName} ${selectedInvite.contactedBy.lastName}`}
              className="w-16 h-16 text-base"
            />
          </button>
          <button
            type="button"
            onClick={() => onViewProfile(selectedInvite.contactedBy.id)}
            className="text-lg font-bold font-['Plus_Jakarta_Sans'] text-gray-900 mb-1 hover:text-blue-600 outline-none focus:outline-none focus:ring-0"
          >
            {selectedInvite.contactedBy.firstName}{" "}
            {selectedInvite.contactedBy.lastName}
          </button>
          <p className="text-sm font-normal font-['Plus_Jakarta_Sans'] text-gray-500 mb-4">
            {selectedInvite.contactedBy.email}
          </p>
          <div className="flex items-center w-full my-4 max-w-lg">
            <div className="flex-grow border-t border-gray-200" />
            <span className="px-4 text-gray-600 text-sm font-normal font-['Plus_Jakarta_Sans'] whitespace-pre-wrap leading-5">
              {selectedInvite.opportunityCardResponseDto
                ? `Hi ${selectedInvite.contacted.firstName} ${selectedInvite.contacted.lastName}! I would like to contact you regarding your Opportunity for ${formatText(selectedInvite.opportunityCardResponseDto.formData?.details?.sport)} with a purse of ${formatText(selectedInvite.opportunityCardResponseDto.formData?.core?.baseAmount)} ${selectedInvite.opportunityCardResponseDto.formData?.core?.currency}.`
                : selectedInvite.readyRosterCardResponseDto
                  ? `Hi ${selectedInvite.contacted.firstName} ${selectedInvite.contacted.lastName}! I would like to contact you regarding your Ready roster for ${formatText(selectedInvite.readyRosterCardResponseDto.sport)} with a purse of ${formatText(selectedInvite.readyRosterCardResponseDto.purse)} ${selectedInvite.readyRosterCardResponseDto.currency}.`
                  : `Hi ${selectedInvite.contacted.firstName || ""} ${selectedInvite.contacted.lastName || ""}! I would like to discuss a potential opportunity with you.`}
            </span>
            <div className="flex-grow border-t border-gray-200" />
          </div>
        </div>
      ) : selectedUser || selectedGroup ? (
        fetchingMessages && sortedMessages.length === 0 ? (
          <div className="flex flex-col gap-4 p-6 justify-end h-full animate-pulse">
            <div className="flex items-start gap-2.5 max-w-[65%]">
              <div className="w-8 h-8 rounded-full bg-slate-200 shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="h-10 bg-slate-200/70 rounded-2xl rounded-tl-sm w-48" />
                <div className="h-2.5 w-12 bg-slate-200/50 rounded ml-1" />
              </div>
            </div>
            <div className="flex items-start gap-2.5 max-w-[65%] self-end flex-row-reverse">
              <div className="space-y-1.5 flex-1 items-end flex flex-col">
                <div className="h-14 bg-blue-100 rounded-2xl rounded-tr-sm w-64" />
                <div className="h-2.5 w-12 bg-slate-200/50 rounded mr-1" />
              </div>
            </div>
            <div className="flex items-start gap-2.5 max-w-[65%]">
              <div className="w-8 h-8 rounded-full bg-slate-200 shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="h-16 bg-slate-200/70 rounded-2xl rounded-tl-sm w-72" />
                <div className="h-2.5 w-12 bg-slate-200/50 rounded ml-1" />
              </div>
            </div>
            <div className="flex items-start gap-2.5 max-w-[65%] self-end flex-row-reverse">
              <div className="space-y-1.5 flex-1 items-end flex flex-col">
                <div className="h-9 bg-blue-100 rounded-2xl rounded-tr-sm w-36" />
                <div className="h-2.5 w-12 bg-slate-200/50 rounded mr-1" />
              </div>
            </div>
          </div>
        ) : sortedMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-700 mb-1">
              {t("startConversation", {
                name: selectedUser
                  ? selectedUser.profileDto.firstName
                  : selectedGroup?.name,
                defaultValue: `Start conversation with ${selectedUser ? selectedUser.profileDto.firstName : selectedGroup?.name}`,
              })}
            </p>
            <p className="text-xs font-normal font-['Plus_Jakarta_Sans'] text-gray-400">
              {t("messagesWillAppearHere", "Messages will appear here")}
            </p>
          </div>
        ) : (
          <div className="space-y-1 max-w-full overflow-hidden">
            {sortedMessages.map((m, i) => renderMessage(m, i))}
            <div ref={messagesEndRef} />
          </div>
        )
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-6 gap-2">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">
            <BsChatRightText className="w-6 h-6" />
          </div>
          <h3 className="text-base font-normal font-['Plus_Jakarta_Sans'] text-gray-700 leading-6">
            {t("selectContactOrGroup", "Select conversation")}
          </h3>
          <p className="text-sm font-normal font-['Plus_Jakarta_Sans'] text-gray-400 leading-5">
            {t("chooseSomeoneToChat", "Choose someone to chat")}
          </p>
        </div>
      )}

      <MessageContent
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
        selectedVideo={selectedVideo}
        connectStatus={connectStatus}
        setConnectStatus={setConnectStatus}
        setSelectedVideo={setSelectedVideo}
        imageCarousel={imageCarousel}
        setImageCarousel={setImageCarousel}
        isModalOnly={true}
      />
    </div>
  );
};
